import React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import { alpha } from '@mui/material/styles';
import MenuRounded from '@mui/icons-material/MenuRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import MailOutlineRounded from '@mui/icons-material/MailOutlineRounded';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';

import Logo from './Logo';
import LanguageSelector from './LangSelector';
import { contactPath } from '../seo/routes';
import { azure, ink, line } from '../theme';

// 'nizro' first: it is the product a visitor can actually start today. It is
// now a real route (/nizro), not a scroll anchor — the rest stay in-page
// anchors that only exist on the home page.
const ANCHOR_SECTIONS = ['services', 'clients', 'about', 'contact'];
const NAV_IDS = ['nizro', ...ANCHOR_SECTIONS];

function scrollToSection(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export default function AppAppBar() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'ar' ? 'ar' : 'en';
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [active, setActive] = React.useState('');
  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 24 });

  // Scrollspy: highlight the section currently in view. There is nothing to
  // observe off the home page, so this only runs there.
  React.useEffect(() => {
    if (!isHome || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: '-38% 0px -55% 0px' }
    );
    ANCHOR_SECTIONS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [isHome]);

  // The four anchor sections only exist on the home page. From elsewhere
  // (currently just /nizro) a click has to navigate back to `/` with the
  // section as a hash, and LandingPage picks that hash up on mount.
  const handleAnchorNav = (id) => {
    setMenuOpen(false);
    if (isHome) {
      scrollToSection(id);
    } else {
      navigate(`/#${id}`);
    }
  };

  const handleNizroNav = () => setMenuOpen(false);

  // Logo: always /, but if we're already home just scroll to top rather than
  // a no-op navigation.
  const handleLogoNav = () => {
    setMenuOpen(false);
    if (isHome) {
      scrollToSection('home');
    } else {
      navigate('/');
    }
  };

  const navButtons = NAV_IDS.map((id) => {
    const isNizroLink = id === 'nizro';
    const isActive = isNizroLink ? location.pathname === '/nizro' : active === id;
    return (
      <Button
        key={id}
        {...(isNizroLink ? { component: RouterLink, to: '/nizro' } : {})}
        variant="text"
        size="small"
        onClick={isNizroLink ? handleNizroNav : () => handleAnchorNav(id)}
        sx={{
          color: isActive ? 'text.primary' : 'text.secondary',
          fontWeight: isActive ? 800 : 600,
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 4,
            insetInlineStart: '50%',
            transform: 'translateX(-50%)',
            width: isActive ? 16 : 0,
            height: 2,
            borderRadius: 1,
            bgcolor: azure[400],
            transition: 'width .25s ease',
          },
        }}
      >
        {t(`nav.${id}`)}
      </Button>
    );
  });

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: scrolled ? alpha(ink.base, 0.78) : 'transparent',
          backgroundImage: 'none',
          backdropFilter: scrolled ? 'blur(16px)' : 'none',
          WebkitBackdropFilter: scrolled ? 'blur(16px)' : 'none',
          borderBottom: `1px solid ${scrolled ? line : 'transparent'}`,
          transition: 'all .3s ease',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ minHeight: { xs: 64, md: 76 }, gap: 2 }}>
            <Box
              onClick={handleLogoNav}
              sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              aria-label="DynaCloud home"
            >
              <Logo size={32} />
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            {/* Desktop nav */}
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{ display: { xs: 'none', md: 'flex' } }}
            >
              {navButtons}
              <Box sx={{ width: '1px', height: 22, bgcolor: line, mx: 1.5 }} />
              <LanguageSelector />
              <Button
                component={RouterLink}
                to={contactPath(lang)}
                variant="contained"
                size="small"
                startIcon={<MailOutlineRounded />}
                sx={{ marginInlineStart: 1.5 }}
              >
                {t('nav.cta')}
              </Button>
            </Stack>

            {/* Mobile trigger */}
            <IconButton
              aria-label={t('nav.menu')}
              onClick={() => setMenuOpen(true)}
              sx={{ display: { xs: 'inline-flex', md: 'none' } }}
            >
              <MenuRounded />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile menu */}
      <Drawer
        anchor="top"
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: alpha(ink.base, 0.96),
            backdropFilter: 'blur(20px)',
            borderBottom: `1px solid ${line}`,
            borderInlineStart: 'none',
            pb: 4,
          },
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ minHeight: 64, justifyContent: 'space-between' }}>
            <Logo size={30} />
            <IconButton aria-label={t('nav.close')} onClick={() => setMenuOpen(false)}>
              <CloseRounded />
            </IconButton>
          </Toolbar>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {NAV_IDS.map((id) => {
              const isNizroLink = id === 'nizro';
              const isActive = isNizroLink ? location.pathname === '/nizro' : active === id;
              return (
                <Button
                  key={id}
                  {...(isNizroLink ? { component: RouterLink, to: '/nizro' } : {})}
                  variant="text"
                  onClick={isNizroLink ? handleNizroNav : () => handleAnchorNav(id)}
                  sx={{
                    justifyContent: 'flex-start',
                    fontSize: '1.15rem',
                    fontWeight: 700,
                    color: isActive ? 'text.primary' : 'text.secondary',
                    px: 1,
                  }}
                >
                  {t(`nav.${id}`)}
                </Button>
              );
            })}
            <Box sx={{ pt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <LanguageSelector />
              <Button
                component={RouterLink}
                to={contactPath(lang)}
                variant="contained"
                startIcon={<MailOutlineRounded />}
                onClick={() => setMenuOpen(false)}
                sx={{ flexGrow: 1 }}
              >
                {t('nav.cta')}
              </Button>
            </Box>
          </Stack>
        </Container>
      </Drawer>
    </>
  );
}
